import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, useEffect, useCallback, useRef } from 'react';
import { CheckCircle2, Circle } from 'lucide-react';
import { useAuth } from '../lib/authContext';
import { api, throwIfNotOk } from '../lib/api';
import { cn } from '@siliconx/shared/ui/utils';
import { LoadingShell, ErrorState } from '@siliconx/shared/ui/state';
import type { OrgAddress, OrgProfile, MeResponse, EscrowOrgStatus } from '@siliconx/shared';
import { Button } from '@siliconx/shared/ui/Button';
import { Card } from '@siliconx/shared/ui/Card';
import { PageHeader } from '@siliconx/shared/ui/PageHeader';
import { PanelTitle } from '@siliconx/shared/ui/PanelTitle';
import { TextInput } from '@siliconx/shared/ui/Field';
import { requireOrgFeature } from '../lib/orgFeatureGate';
import { DataSubscriptionPanel } from '../components/data/DataSubscriptionPanel';
import { ApiKeysPanel } from '../components/data/ApiKeysPanel';
import { EscrowPaymentsPanel, ESCROW_EMAIL_LOCKED_MESSAGE } from '../components/profile/EscrowPaymentsPanel';

/**
 * RUF-540: mirrors the shared profileCompleteness() predicate's field keys
 * (packages/shared/src/dto/client-profile.ts PROFILE_COMPLETENESS_COLUMNS) —
 * the CTA landing spot for ProfileLockedCard's "Complete profile" link.
 */
const COMPLETENESS_FIELDS: { key: string; label: string }[] = [
  { key: 'legalName', label: 'Legal name' },
  { key: 'contactEmail', label: 'Contact email' },
  { key: 'contactPhone', label: 'Contact phone' },
  { key: 'address', label: 'Address (street, city, country)' },
];

function CompletenessChecklist({ complete, missingFields }: { complete: boolean; missingFields: string[] }) {
  return (
    <div className="rounded-md border border-border bg-muted p-3">
      <p className={cn('mb-2 text-xs font-medium', complete ? 'text-green-700' : 'text-amber-700')}>
        {complete ? 'Profile complete — ready to transact' : 'Complete these fields to unlock this profile'}
      </p>
      <ul className="space-y-1">
        {COMPLETENESS_FIELDS.map(({ key, label }) => {
          const done = !missingFields.includes(key);
          return (
            <li key={key} className="flex items-center gap-2 text-xs">
              {done ? (
                <CheckCircle2 className="size-3.5 shrink-0 text-green-600" />
              ) : (
                <Circle className="size-3.5 shrink-0 text-muted-foreground" />
              )}
              <span className={done ? 'text-muted-foreground' : 'text-muted-foreground'}>{label}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

const LOGO_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const LOGO_MAX_BYTES = 10 * 1024 * 1024;

// SIL-1108: `orgFeature: 'orgProfile'` (customerNav.ts) is business-only —
// the nav entry remaps an individual org to /individual-profile instead
// (`fallbackTo`, customerNav.ts), but a direct hit on this URL still needs
// this route's own mount-time fetches to not fire before the root
// `OrgFeatureGate`'s (__root.tsx) redirect to /dashboard lands (unchanged
// destination — the fallback is a nav-only remap, not this route's gate).
export const Route = createFileRoute('/profile')({
  beforeLoad: () => requireOrgFeature('orgProfile'),
  component: ProfilePage,
});

// RUF-270: catalog preference types
interface CatalogItem {
  id: string;
  name: string;
}

interface CatalogReference {
  assetClasses: CatalogItem[];
  manufacturers: CatalogItem[];
  regions: CatalogItem[];
}

/** RUF-271: fixed enum slugs — no fetch needed */
const SHIPPING_METHOD_OPTIONS = [
  { value: 'freight',       label: 'Freight' },
  { value: 'palletized',    label: 'Palletized' },
  { value: 'white_glove',   label: 'White Glove' },
  { value: 'parcel',        label: 'Parcel' },
  { value: 'international', label: 'International' },
] as const;

const DC_CAPABILITY_OPTIONS = [
  { value: 'standard_density', label: 'Standard Density' },
  { value: 'high_density',     label: 'High Density' },
  { value: 'air_cooled',       label: 'Air Cooled' },
  { value: 'liquid_cooled',    label: 'Liquid Cooled' },
  { value: 'colo',             label: 'Colocation' },
  { value: 'on_prem',          label: 'On-Premises' },
] as const;

/** RUF-333: fixed services allowlist — store now, filter later */
const SERVICE_OPTIONS = [
  { value: 'data_wipe',     label: 'Data Wipe' },
  { value: 'asset_tagging', label: 'Asset Tagging' },
  { value: 'install',       label: 'Install' },
  { value: 'decommission',  label: 'Decommission' },
] as const;

type ShippingMethodSlug = (typeof SHIPPING_METHOD_OPTIONS)[number]['value'];
type DcCapabilitySlug   = (typeof DC_CAPABILITY_OPTIONS)[number]['value'];
type ServiceSlug        = (typeof SERVICE_OPTIONS)[number]['value'];

// OrgProfile, MeResponse (as ProfileData below) — imported from @siliconx/shared,
// field-identical to the shapes formerly declared here (SIL-481).

function roleBadgeCls(role: 'admin' | 'member') {
  return cn(
    'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
    role === 'admin'
      ? 'bg-brand-100 text-brand-700'
      : 'bg-muted text-muted-foreground',
  );
}

/** Read-only display field */
function ReadOnlyField({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div>
      <label className="block text-xs font-medium text-muted-foreground mb-1">{label}</label>
      <div className="rounded-md border border-border bg-muted px-3 py-2 text-sm text-foreground">
        {value || '—'}
      </div>
    </div>
  );
}

/** Editable text input */
function TextField({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  disabled,
  hint,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  disabled?: boolean;
  hint?: string;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-muted-foreground mb-1">{label}</label>
      <TextInput className="focus:ring-2 placeholder:text-muted-foreground text-foreground"
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
      />
      {hint && <p className="mt-1 text-xs text-amber-700">{hint}</p>}
    </div>
  );
}

/** Editable textarea */
function TextAreaField({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-muted-foreground mb-1">{label}</label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        placeholder={placeholder}
        className="w-full rounded-md border border-border px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none"
      />
    </div>
  );
}

/**
 * Unified multi-select control (RUF-270 + RUF-271).
 *
 * Serves both preference styles from a single component:
 *   - variant="checkbox": scrollable checkbox list (catalog preferences, RUF-270)
 *   - variant="pills":     wrap-around toggle pills (fixed enum logistics, RUF-271)
 *
 * `readOnly` renders the selected option labels joined as static text.
 */
function MultiSelectField<T extends string>({
  label,
  options,
  selected,
  onChange,
  readOnly = false,
  variant = 'pills',
}: {
  label: string;
  options: ReadonlyArray<{ value: T; label: string }>;
  selected: T[];
  onChange?: (next: T[]) => void;
  readOnly?: boolean;
  variant?: 'checkbox' | 'pills';
}) {
  function toggle(value: T) {
    if (readOnly || !onChange) return;
    onChange(
      selected.includes(value)
        ? selected.filter((v) => v !== value)
        : [...selected, value],
    );
  }

  if (readOnly) {
    const selectedLabels = options
      .filter((o) => selected.includes(o.value))
      .map((o) => o.label);
    return (
      <div>
        <label className="block text-xs font-medium text-muted-foreground mb-1">{label}</label>
        <div className="rounded-md border border-border bg-muted px-3 py-2 text-sm text-foreground">
          {selectedLabels.length > 0 ? selectedLabels.join(', ') : '—'}
        </div>
      </div>
    );
  }

  if (variant === 'checkbox') {
    return (
      <div>
        <label className="block text-xs font-medium text-muted-foreground mb-1">{label}</label>
        <div className="rounded-md border border-border bg-card p-2 max-h-40 overflow-y-auto space-y-1">
          {options.length === 0 && (
            <p className="text-xs text-muted-foreground px-1">No options available</p>
          )}
          {options.map((item) => (
            <label
              key={item.value}
              className="flex items-center gap-2 px-1 py-0.5 rounded hover:bg-muted cursor-pointer"
            >
              <input
                type="checkbox"
                checked={selected.includes(item.value)}
                onChange={() => toggle(item.value)}
                className="rounded border-border text-brand-600 focus:ring-brand-500"
              />
              <span className="text-sm text-foreground">{item.label}</span>
            </label>
          ))}
        </div>
      </div>
    );
  }

  // variant === 'pills'
  return (
    <div>
      <label className="block text-xs font-medium text-muted-foreground mb-2">{label}</label>
      <div className="flex flex-wrap gap-2">
        {options.map(({ value, label: optLabel }) => {
          const checked = selected.includes(value);
          return (
            <button
              key={value}
              type="button"
              onClick={() => toggle(value)}
              className={cn(
                'inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                checked
                  ? 'border-brand-500 bg-brand-50 text-brand-700'
                  : 'border-border bg-card text-muted-foreground hover:border-border',
              )}
            >
              {optLabel}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Read-only pill list for multi-select values */
function ReadOnlyPillList({ label, values }: { label: string; values: string[] }) {
  return (
    <div>
      <label className="block text-xs font-medium text-muted-foreground mb-2">{label}</label>
      {values.length === 0 ? (
        <div className="text-sm text-muted-foreground">—</div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {values.map((v) => (
            <span
              key={v}
              className="inline-flex items-center rounded-full border border-border bg-muted px-3 py-1 text-xs font-medium text-foreground"
            >
              {v}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * OrgProfileSection — renders a single editable/read-only org profile card.
 * Used for both the Seller profile and Buyer profile panels (RUF-331).
 * Each section manages its own form state and saves independently via
 * PATCH /me/org with the `target` discriminant.
 */
export function OrgProfileSection({
  title,
  target,
  profile,
  isAdmin,
  catalogRef,
  onSaved,
  complete,
  missingFields,
  contactEmailLocked = false,
}: {
  title: string;
  target: 'seller' | 'buyer';
  profile: OrgProfile | null;
  isAdmin: boolean;
  catalogRef: CatalogReference;
  onSaved: (updated: OrgProfile, target: 'seller' | 'buyer') => void;
  complete: boolean;
  missingFields: string[];
  /**
   * SIL-2169: true while the org has an open Escrow.com transaction. The
   * contact email is what Escrow.com matches parties by, so the edit is
   * blocked in the UI; the database refuses the change too (EC008 -> 409).
   */
  contactEmailLocked?: boolean;
}) {
  const poolNoteLabel    = target === 'seller' ? 'Fleet note'                  : 'Procurement note';
  const assetClassLabel   = target === 'seller' ? 'Asset classes you deal in'   : 'Asset classes you are seeking';
  const manufacturerLabel = target === 'seller' ? 'Manufacturers you carry'     : 'Manufacturers you prefer';
  const regionLabel       = target === 'seller' ? 'Regions you operate in'      : 'Regions you source from';

  const [legalName, setLegalName]           = useState(profile?.legalName ?? '');
  const [shortName, setShortName]           = useState(profile?.shortName ?? '');
  const [contactEmail, setContactEmail]     = useState(profile?.contactEmail ?? '');
  const [contactPhone, setContactPhone]     = useState(profile?.contactPhone ?? '');
  const [website, setWebsite]               = useState(profile?.website ?? '');
  const [description, setDescription]       = useState(profile?.description ?? '');
  const [poolNote, setPoolNote]             = useState(profile?.poolNote ?? '');
  const [addrStreet, setAddrStreet]         = useState(profile?.address?.street ?? '');
  const [addrCity, setAddrCity]             = useState(profile?.address?.city ?? '');
  const [addrState, setAddrState]           = useState(profile?.address?.state ?? '');
  const [addrPostal, setAddrPostal]         = useState(profile?.address?.postal ?? '');
  const [addrCountry, setAddrCountry]       = useState(profile?.address?.country ?? '');
  const [prefAssetClasses, setPrefAssetClasses]       = useState<string[]>(profile?.prefAssetClasses ?? []);
  const [prefManufacturers, setPrefManufacturers]     = useState<string[]>(profile?.prefManufacturers ?? []);
  const [prefRegions, setPrefRegions]                 = useState<string[]>(profile?.prefRegions ?? []);
  const [prefShippingMethods, setPrefShippingMethods] = useState<ShippingMethodSlug[]>(profile?.prefShippingMethods ?? []);
  const [prefDcCapability, setPrefDcCapability]       = useState<DcCapabilitySlug[]>(profile?.prefDcCapability ?? []);
  const [prefServices, setPrefServices]               = useState<ServiceSlug[]>(profile?.prefServices ?? []);

  const [saving, setSaving]   = useState(false);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);

  // Sync form state if the profile prop changes (e.g. after parent re-fetches)
  useEffect(() => {
    if (!profile) return;
    setLegalName(profile.legalName ?? '');
    setShortName(profile.shortName ?? '');
    setContactEmail(profile.contactEmail ?? '');
    setContactPhone(profile.contactPhone ?? '');
    setWebsite(profile.website ?? '');
    setDescription(profile.description ?? '');
    setPoolNote(profile.poolNote ?? '');
    setAddrStreet(profile.address?.street ?? '');
    setAddrCity(profile.address?.city ?? '');
    setAddrState(profile.address?.state ?? '');
    setAddrPostal(profile.address?.postal ?? '');
    setAddrCountry(profile.address?.country ?? '');
    setPrefAssetClasses(profile.prefAssetClasses ?? []);
    setPrefManufacturers(profile.prefManufacturers ?? []);
    setPrefRegions(profile.prefRegions ?? []);
    setPrefShippingMethods(profile.prefShippingMethods ?? []);
    setPrefDcCapability(profile.prefDcCapability ?? []);
    setPrefServices(profile.prefServices ?? []);
  }, [profile]);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setSaveMsg(null);
    try {
      const address: NonNullable<OrgAddress> = {};
      if (addrStreet)  address.street  = addrStreet;
      if (addrCity)    address.city    = addrCity;
      if (addrState)   address.state   = addrState;
      if (addrPostal)  address.postal  = addrPostal;
      if (addrCountry) address.country = addrCountry;

      const payload = {
        target,
        legalName:    legalName   || undefined,
        shortName:    shortName   || null,
        contactEmail: contactEmail || null,
        contactPhone: contactPhone || null,
        website:      website     || null,
        description:  description || null,
        address:      Object.keys(address).length > 0 ? address : null,
        poolNote:     poolNote    || null,
        prefAssetClasses,
        prefManufacturers,
        prefRegions,
        prefShippingMethods,
        prefDcCapability,
        prefServices,
      };

      const res = await api.api.client.me.org.$patch({ json: payload });
      if (!res.ok) {
        await throwIfNotOk(res);
        throw new Error(`HTTP ${res.status}`);
      }
      const body = (await res.json()) as unknown as { orgProfile: OrgProfile };
      onSaved(body.orgProfile, target);
      // Sync preference state from server response
      setPrefAssetClasses(body.orgProfile.prefAssetClasses ?? []);
      setPrefManufacturers(body.orgProfile.prefManufacturers ?? []);
      setPrefRegions(body.orgProfile.prefRegions ?? []);
      setPrefServices(body.orgProfile.prefServices ?? []);
      setSaveMsg('Saved.');
    } catch (err: unknown) {
      // SIL-2169: the server refuses a contact email change while an Escrow.com transaction is open
      // (409 escrow_email_locked). Show its fixed message; every other failure stays generic.
      const e = err as { status?: number; message?: string };
      setSaveMsg(e.status === 409 && e.message ? e.message : 'Failed to save.');
    } finally {
      setSaving(false);
    }
  }

  const op = profile;

  return (
    <Card className="space-y-5">
      <PanelTitle
        title={title}
        right={
          !isAdmin && <span className="text-xs text-muted-foreground">Only admins can edit org details</span>
        }
      />

      {/* RUF-540: per-side completeness checklist — the CTA landing spot linked
          from ProfileLockedCard. onboardingCompleted is orthogonal (first-run
          tour state) and not shown here. */}
      <CompletenessChecklist complete={complete} missingFields={missingFields} />

      {isAdmin ? (
        <form onSubmit={handleSave} className="space-y-4">
          <TextField
            label="Legal name"
            value={legalName}
            onChange={setLegalName}
            placeholder="Acme Inc."
          />
          <TextField
            label="Short name"
            value={shortName}
            onChange={setShortName}
            placeholder="Acme"
          />
          <TextField
            label="Contact email"
            value={contactEmail}
            onChange={setContactEmail}
            type="email"
            placeholder="ops@acme.com"
            disabled={contactEmailLocked}
            hint={contactEmailLocked ? ESCROW_EMAIL_LOCKED_MESSAGE : undefined}
          />
          <TextField
            label="Contact phone"
            value={contactPhone}
            onChange={setContactPhone}
            type="tel"
            placeholder="+1 555 000 0000"
          />
          <TextField
            label="Website"
            value={website}
            onChange={setWebsite}
            type="url"
            placeholder="https://acme.com"
          />
          <TextAreaField
            label="Description"
            value={description}
            onChange={setDescription}
            placeholder="Brief description of your organization…"
          />

          {/* Address */}
          <div className="pt-2 border-t border-border space-y-3">
            <p className="text-xs font-medium text-muted-foreground">Address</p>
            <TextField label="Street" value={addrStreet} onChange={setAddrStreet} placeholder="123 Main St" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <TextField label="City" value={addrCity} onChange={setAddrCity} placeholder="San Francisco" />
              <TextField label="State" value={addrState} onChange={setAddrState} placeholder="CA" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <TextField label="Postal code" value={addrPostal} onChange={setAddrPostal} placeholder="94103" />
              <TextField label="Country" value={addrCountry} onChange={setAddrCountry} placeholder="US" />
            </div>
          </div>

          <TextAreaField
            label={poolNoteLabel}
            value={poolNote}
            onChange={setPoolNote}
            placeholder="Visible note for your organization…"
          />

          {/* RUF-270: catalog preferences */}
          <div className="pt-2 border-t border-border space-y-3">
            <p className="text-xs font-medium text-muted-foreground">Catalog preferences</p>
            <MultiSelectField
              label={assetClassLabel}
              variant="checkbox"
              options={catalogRef.assetClasses.map((c) => ({ value: c.id, label: c.name }))}
              selected={prefAssetClasses}
              onChange={setPrefAssetClasses}
            />
            <MultiSelectField
              label={manufacturerLabel}
              variant="checkbox"
              options={catalogRef.manufacturers.map((c) => ({ value: c.id, label: c.name }))}
              selected={prefManufacturers}
              onChange={setPrefManufacturers}
            />
            <MultiSelectField
              label={regionLabel}
              variant="checkbox"
              options={catalogRef.regions.map((c) => ({ value: c.id, label: c.name }))}
              selected={prefRegions}
              onChange={setPrefRegions}
            />
          </div>

          {/* RUF-271: logistics preferences */}
          <div className="pt-2 border-t border-border space-y-4">
            <p className="text-xs font-medium text-muted-foreground">Logistics preferences</p>
            <MultiSelectField
              label={
                target === 'seller'
                  ? 'Shipping methods — supply capability'
                  : 'Shipping methods — seeking'
              }
              options={SHIPPING_METHOD_OPTIONS}
              selected={prefShippingMethods}
              onChange={setPrefShippingMethods}
            />
            <MultiSelectField
              label={
                target === 'seller'
                  ? 'DC capability — can support'
                  : 'DC capability — can support / seeking'
              }
              options={DC_CAPABILITY_OPTIONS}
              selected={prefDcCapability}
              onChange={setPrefDcCapability}
            />
          </div>

          {/* RUF-333: services preferences (store now, filter later) */}
          <div className="pt-2 border-t border-border space-y-4">
            <p className="text-xs font-medium text-muted-foreground">Services preferences</p>
            <MultiSelectField
              label={
                target === 'seller'
                  ? 'Services you provide'
                  : 'Services you require'
              }
              options={SERVICE_OPTIONS}
              selected={prefServices}
              onChange={setPrefServices}
            />
          </div>

          <div className="flex items-center gap-3 pt-2 border-t border-border">
            <Button
              type="submit"
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
            {saveMsg && (
              <span className={cn('text-sm', saveMsg === 'Saved.' ? 'text-muted-foreground' : 'text-red-600')}>
                {saveMsg}
              </span>
            )}
          </div>
        </form>
      ) : (
        /* Member view — read-only */
        <div className="space-y-4">
          <ReadOnlyField label="Legal name" value={op?.legalName} />
          <ReadOnlyField label="Short name" value={op?.shortName} />
          <ReadOnlyField label="Contact email" value={op?.contactEmail} />
          <ReadOnlyField label="Contact phone" value={op?.contactPhone} />
          <ReadOnlyField label="Website" value={op?.website} />
          <ReadOnlyField label="Description" value={op?.description} />
          {op?.address && (
            <ReadOnlyField
              label="Address"
              value={[op.address.street, op.address.city, op.address.state, op.address.postal, op.address.country]
                .filter(Boolean)
                .join(', ')}
            />
          )}
          <ReadOnlyField label={poolNoteLabel} value={op?.poolNote} />
          {/* RUF-270: catalog preferences (read-only for members) */}
          <div className="pt-2 border-t border-border space-y-3">
            <p className="text-xs font-medium text-muted-foreground">Catalog preferences</p>
            <MultiSelectField
              label={assetClassLabel}
              variant="checkbox"
              readOnly
              options={catalogRef.assetClasses.map((c) => ({ value: c.id, label: c.name }))}
              selected={op?.prefAssetClasses ?? []}
            />
            <MultiSelectField
              label={manufacturerLabel}
              variant="checkbox"
              readOnly
              options={catalogRef.manufacturers.map((c) => ({ value: c.id, label: c.name }))}
              selected={op?.prefManufacturers ?? []}
            />
            <MultiSelectField
              label={regionLabel}
              variant="checkbox"
              readOnly
              options={catalogRef.regions.map((c) => ({ value: c.id, label: c.name }))}
              selected={op?.prefRegions ?? []}
            />
          </div>
          {/* RUF-271: logistics preferences read-only */}
          <ReadOnlyPillList
            label={
              target === 'seller'
                ? 'Shipping methods — supply capability'
                : 'Shipping methods — seeking'
            }
            values={op?.prefShippingMethods ?? []}
          />
          <ReadOnlyPillList
            label={
              target === 'seller'
                ? 'DC capability — can support'
                : 'DC capability — can support / seeking'
            }
            values={op?.prefDcCapability ?? []}
          />
          {/* RUF-333: services preferences read-only */}
          <ReadOnlyPillList
            label={
              target === 'seller'
                ? 'Services you provide'
                : 'Services you require'
            }
            values={op?.prefServices ?? []}
          />
        </div>
      )}
    </Card>
  );
}

// --------------------------------------------------------------------------
// SIL-171: direct-offers seller settings — opt-in to unsolicited buyer
// direct offers + a minimum-above-MCMP threshold. Lives on customer_org (654),
// saved via the SAME PATCH /me/org endpoint as the seller profile form
// (target: 'seller') but as its own small card + independent save, mirroring
// how the other settings cards save independently of OrgProfileSection.
// --------------------------------------------------------------------------
function DirectOfferSettingsSection({
  acceptUnsolicitedOffers,
  unsolicitedOfferThresholdPct,
  isAdmin,
  onSaved,
}: {
  acceptUnsolicitedOffers: boolean;
  unsolicitedOfferThresholdPct: number;
  isAdmin: boolean;
  onSaved: (settings: { acceptUnsolicitedOffers: boolean; unsolicitedOfferThresholdPct: number }) => void;
}) {
  const [enabled, setEnabled] = useState(acceptUnsolicitedOffers);
  const [thresholdStr, setThresholdStr] = useState(String(unsolicitedOfferThresholdPct));
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);

  useEffect(() => {
    setEnabled(acceptUnsolicitedOffers);
    setThresholdStr(String(unsolicitedOfferThresholdPct));
  }, [acceptUnsolicitedOffers, unsolicitedOfferThresholdPct]);

  const threshold = Number(thresholdStr);
  const thresholdValid = Number.isInteger(threshold) && threshold >= 0 && threshold <= 100;

  async function handleSave() {
    if (!thresholdValid) return;
    setSaving(true);
    setSaveMsg(null);
    try {
      const res = await api.api.client.me.org.$patch({
        json: {
          target: 'seller',
          acceptUnsolicitedOffers: enabled,
          unsolicitedOfferThresholdPct: threshold,
        },
      });
      if (!res.ok) {
        await throwIfNotOk(res);
        throw new Error(`HTTP ${res.status}`);
      }
      onSaved({ acceptUnsolicitedOffers: enabled, unsolicitedOfferThresholdPct: threshold });
      setSaveMsg('Saved.');
    } catch {
      setSaveMsg('Failed to save.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card className="space-y-4">
      <PanelTitle
        title="Unsolicited direct offers"
        description="When enabled, buyers can send direct offers against your inventory that you did not list — only offers clearing your threshold above the market comp price show up under Opportunities."
      />

      {isAdmin ? (
        <div className="space-y-4">
          <label className="flex items-center gap-2 text-sm text-foreground">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="rounded border-border text-brand-600 focus:ring-brand-500"
            />
            Accept unsolicited direct offers
          </label>

          <div className="max-w-xs">
            <label className="block text-xs font-medium text-muted-foreground mb-1">
              Minimum threshold above market comp price (%)
            </label>
            <TextInput className="disabled:text-muted-foreground focus:ring-2 text-foreground"
              type="number"
              min="0"
              max="100"
              step="1"
              value={thresholdStr}
              onChange={(e) => setThresholdStr(e.target.value)}
              disabled={!enabled}
            />
            {!thresholdValid && (
              <p className="mt-1 text-xs text-red-600">Must be an integer between 0 and 100.</p>
            )}
          </div>

          <div className="flex items-center gap-3 pt-2 border-t border-border">
            <Button
              onClick={handleSave}
              disabled={saving || !thresholdValid}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
            {saveMsg && (
              <span className={cn('text-sm', saveMsg === 'Saved.' ? 'text-muted-foreground' : 'text-red-600')}>
                {saveMsg}
              </span>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <ReadOnlyField
            label="Accept unsolicited direct offers"
            value={acceptUnsolicitedOffers ? 'Enabled' : 'Disabled'}
          />
          <ReadOnlyField
            label="Minimum threshold above market comp price"
            value={`${unsolicitedOfferThresholdPct}%`}
          />
        </div>
      )}
    </Card>
  );
}

// --------------------------------------------------------------------------
// SIL-379 (the org rung of SIL-356): org-level PUE input. Lives directly on customer_org (708), NOT
// tied to seller/buyer target the way OrgProfileSection is — this is a
// single org-wide facility-efficiency figure, so it gets its own dedicated
// PATCH/DELETE /me/pue endpoints (mirrors BrandingSection's own dedicated
// /me/logo endpoints) rather than folding into PATCH /me/org.
//
// Supplying a real PUE here is what moves an asset off the
// 'screening_estimate_default_pue' reporting-eligibility value and up
// toward the 'measured' accuracy tier (deriveUsePhaseAccuracyTier /
// isPueSupplied, packages/shared/src/use-phase/accuracy-tier.ts) — the whole
// point of SIL-379. Out-of-range values are rejected (400), never clamped;
// clamping would silently understate a real high-PUE facility.
// --------------------------------------------------------------------------
const PUE_MIN = 1.0;
const PUE_MAX = 3.0;

function PueSettingsSection({
  pue,
  pueUpdatedAt,
  isAdmin,
  onSaved,
}: {
  pue: number | null;
  pueUpdatedAt: string | null;
  isAdmin: boolean;
  onSaved: (settings: { pue: number | null; pueUpdatedAt: string | null }) => void;
}) {
  const [valueStr, setValueStr] = useState(pue != null ? String(pue) : '');
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);

  useEffect(() => {
    setValueStr(pue != null ? String(pue) : '');
  }, [pue]);

  const parsed = valueStr.trim() === '' ? null : Number(valueStr);
  const valid = parsed === null || (Number.isFinite(parsed) && parsed >= PUE_MIN && parsed <= PUE_MAX);

  async function handleSave() {
    if (!valid) return;
    setSaving(true);
    setSaveMsg(null);
    try {
      if (parsed === null) {
        const res = await api.api.client.me.pue.$delete();
        if (!res.ok) {
          await throwIfNotOk(res);
          throw new Error(`HTTP ${res.status}`);
        }
        onSaved({ pue: null, pueUpdatedAt: null });
      } else {
        const res = await api.api.client.me.pue.$patch({ json: { pue: parsed } });
        if (!res.ok) {
          await throwIfNotOk(res);
          throw new Error(`HTTP ${res.status}`);
        }
        const body = (await res.json()) as unknown as { pue: number | null; pueUpdatedAt: string | null };
        onSaved(body);
      }
      setSaveMsg('Saved.');
    } catch (ex: unknown) {
      setSaveMsg((ex as Error).message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card className="space-y-4">
      <PanelTitle
        title="Facility efficiency (PUE)"
        description={`Power Usage Effectiveness for your facility (total facility power ÷ IT power). Supplying your real figure improves the accuracy of your use-phase carbon estimates — without it, we use a published industry-average PUE. Must be between ${PUE_MIN.toFixed(1)} and ${PUE_MAX.toFixed(1)}.`}
      />

      {isAdmin ? (
        <div className="space-y-4">
          <div className="max-w-xs">
            <label className="block text-xs font-medium text-muted-foreground mb-1">PUE</label>
            <TextInput className="disabled:text-muted-foreground focus:ring-2 text-foreground"
              type="number"
              min={PUE_MIN}
              max={PUE_MAX}
              step="0.01"
              placeholder={`e.g. 1.4 (leave blank to use the industry default)`}
              value={valueStr}
              onChange={(e) => setValueStr(e.target.value)}
            />
            {!valid && (
              <p className="mt-1 text-xs text-red-600">
                Must be between {PUE_MIN.toFixed(1)} and {PUE_MAX.toFixed(1)}.
              </p>
            )}
            {pueUpdatedAt && (
              <p className="mt-1 text-xs text-muted-foreground">
                Last updated {new Date(pueUpdatedAt).toLocaleDateString()}
              </p>
            )}
          </div>

          <div className="flex items-center gap-3 pt-2 border-t border-border">
            <Button onClick={handleSave} disabled={saving || !valid}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
            {saveMsg && (
              <span className={cn('text-sm', saveMsg === 'Saved.' ? 'text-muted-foreground' : 'text-red-600')}>
                {saveMsg}
              </span>
            )}
          </div>
        </div>
      ) : (
        <ReadOnlyField label="PUE" value={pue != null ? String(pue) : 'Not supplied (using industry default)'} />
      )}
    </Card>
  );
}

function Toast({
  message,
  type,
  onDismiss,
}: {
  message: string;
  type: 'success' | 'error';
  onDismiss: () => void;
}) {
  useEffect(() => {
    const t = setTimeout(onDismiss, 4000);
    return () => clearTimeout(t);
  }, [onDismiss]);
  return (
    <div
      className={cn(
        'fixed bottom-6 right-6 z-50 rounded-lg px-4 py-3 text-sm font-medium shadow-lg',
        type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white',
      )}
    >
      {message}
    </div>
  );
}

// --------------------------------------------------------------------------
// Organization branding — logo upload (two-phase presign → PUT → finalize).
// --------------------------------------------------------------------------
function BrandingSection({ notify }: { notify: (m: string, t: 'success' | 'error') => void }) {
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    (async () => {
      const res = await api.api.client.me.$get();
      await throwIfNotOk(res);
      const me = (await res.json()) as unknown as { logoUrl: string | null };
      setLogoUrl(me.logoUrl);
    })().catch(() => {});
  }, []);

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = ''; // allow re-selecting same file
    if (!file) return;
    if (!LOGO_TYPES.includes(file.type)) {
      notify('Logo must be a JPEG, PNG, or WebP image', 'error');
      return;
    }
    if (file.size > LOGO_MAX_BYTES) {
      notify('Logo must be 10 MB or smaller', 'error');
      return;
    }
    setBusy(true);
    try {
      // 1. presign
      const presignRes = await api.api.client.me.logo.$post({
        json: { contentType: file.type, contentLength: file.size },
      });
      await throwIfNotOk(presignRes);
      const { uploadUrl, s3Key } = (await presignRes.json()) as unknown as {
        uploadUrl: string;
        s3Key: string;
      };
      // 2. PUT the raw bytes straight to S3 (no auth header on the presigned URL).
      const put = await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': file.type }, body: file });
      if (!put.ok) throw new Error(`Upload failed (HTTP ${put.status})`);
      // 3. finalize → persists the key, returns the CDN URL.
      const finalizeRes = await api.api.client.me.logo.finalize.$post({ json: { s3Key } });
      await throwIfNotOk(finalizeRes);
      const { logoUrl: url } = (await finalizeRes.json()) as unknown as { logoUrl: string };
      setLogoUrl(url);
      notify('Logo updated', 'success');
    } catch (ex: unknown) {
      notify((ex as Error).message, 'error');
    } finally {
      setBusy(false);
    }
  }

  async function handleRemove() {
    setBusy(true);
    try {
      const res = await api.api.client.me.logo.$delete();
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setLogoUrl(null);
      notify('Logo removed', 'success');
    } catch (ex: unknown) {
      notify((ex as Error).message, 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="space-y-3">
      <PanelTitle title="Organization Branding" />
      <div className="flex items-center gap-4 rounded-lg border border-border bg-muted p-4">
        <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border bg-card">
          {logoUrl ? (
            <img src={logoUrl} alt="Org logo" className="h-full w-full object-contain" />
          ) : (
            <span className="text-xs text-muted-foreground">No logo</span>
          )}
        </div>
        <div className="space-y-1">
          <p className="text-xs text-muted-foreground">Shown in your portal header. JPEG, PNG, or WebP up to 10 MB.</p>
          <div className="flex gap-2">
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleFile} />
            <Button size="sm" className="text-sm"
              disabled={busy}
              onClick={() => fileRef.current?.click()}
            >
              {busy ? 'Working…' : logoUrl ? 'Replace' : 'Upload logo'}
            </Button>
            {logoUrl && (
              <button type="button" disabled={busy} onClick={handleRemove} className="rounded-md px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50">
                Remove
              </button>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}

function ProfilePage() {
  const {
    isLoading: authLoading,
    canSell,
    canBuy,
    sellerMissingFields,
    buyerMissingFields,
  } = useAuth();
  const [profile, setProfile] = useState<MeResponse | null>(null);
  // SIL-2169: per-side "has a live Escrow.com transaction" (null = unknown: no warning, no lock).
  const [escrowStatus, setEscrowStatus] = useState<EscrowOrgStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const notify = useCallback((message: string, type: 'success' | 'error') => setToast({ message, type }), []);

  // RUF-270: catalog reference data for preference multi-selects
  const [catalogRef, setCatalogRef] = useState<CatalogReference>({
    assetClasses: [],
    manufacturers: [],
    regions: [],
  });

  useEffect(() => {
    // Fetch profile and catalog reference data in parallel
    (async () => {
      const mePromise: Promise<Response> = api.api.client.me.$get();
      const refPromise: Promise<Response> = api.api.client.reference.$get();
      // SIL-2169: a failure here must not break the page; it only leaves the lock unknown (the server still enforces it).
      const escrowPromise: Promise<Response | null> = api.api.client.me['escrow-status'].$get().catch(() => null);
      const [meRes, refRes, escrowRes] = await Promise.all([mePromise, refPromise, escrowPromise]);
      if (escrowRes?.ok) {
        setEscrowStatus((await escrowRes.json()) as unknown as EscrowOrgStatus);
      }
      if (!meRes.ok) throw new Error(`HTTP ${meRes.status}`);
      const profileData = (await meRes.json()) as unknown as MeResponse;
      const refData = refRes.ok
        ? ((await refRes.json()) as unknown as CatalogReference)
        : { assetClasses: [], manufacturers: [], regions: [] };
      setProfile(profileData);
      setCatalogRef(refData);
    })()
      .catch((err: unknown) => setError((err as Error).message))
      .finally(() => setLoading(false));
  }, []);

  function handleProfileSaved(updated: OrgProfile, target: 'seller' | 'buyer') {
    setProfile((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        ...(target === 'seller' ? { sellerProfile: updated } : { buyerProfile: updated }),
        // Keep orgName in sync for the legacy orgType field
        orgName: target === prev.orgType ? updated.legalName : prev.orgName,
      };
    });
  }

  function handleDirectOfferSettingsSaved(settings: { acceptUnsolicitedOffers: boolean; unsolicitedOfferThresholdPct: number }) {
    setProfile((prev) => (prev ? { ...prev, ...settings } : prev));
  }

  function handlePueSaved(settings: { pue: number | null; pueUpdatedAt: string | null }) {
    setProfile((prev) => (prev ? { ...prev, ...settings } : prev));
  }

  if (authLoading || loading) {
    return <LoadingShell rows={6} />;
  }

  if (error) {
    return <ErrorState error={error} />;
  }

  if (!profile) return null;

  const isAdmin = profile.role === 'admin';

  return (
    <>
      <div className="mx-auto max-w-lg space-y-6">
        <PageHeader
          title="Settings"
          pinTo="/profile"
          actions={
            isAdmin && (
              <Link
                to="/team"
                className="rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700"
              >
                Manage Team
              </Link>
            )
          }
        />

        {/* Personal info — always read-only */}
        <Card className="space-y-4">
          <PanelTitle title="Your account" />
          <ReadOnlyField label="Email" value={profile.email} />
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Role</label>
            <span className={roleBadgeCls(profile.role)}>{profile.role}</span>
          </div>
        </Card>

        {/* Org logo — admin-only (any org admin, seller or buyer). */}
        {isAdmin && <BrandingSection notify={notify} />}

        {/*
          RUF-331: Two always-present profile sections.
          Both are always visible; each saves independently with its own target.
          No persona switch — both surfaces are always rendered.
        */}
        <OrgProfileSection
          title="Seller profile"
          target="seller"
          profile={profile.sellerProfile ?? null}
          isAdmin={isAdmin}
          catalogRef={catalogRef}
          onSaved={handleProfileSaved}
          complete={canSell}
          missingFields={sellerMissingFields}
          contactEmailLocked={escrowStatus?.seller ?? false}
        />

        {/* SIL-171: seller-only direct-offers opt-in + threshold. */}
        <DirectOfferSettingsSection
          acceptUnsolicitedOffers={profile.acceptUnsolicitedOffers}
          unsolicitedOfferThresholdPct={profile.unsolicitedOfferThresholdPct}
          isAdmin={isAdmin}
          onSaved={handleDirectOfferSettingsSaved}
        />

        <OrgProfileSection
          title="Buyer profile"
          target="buyer"
          profile={profile.buyerProfile ?? null}
          isAdmin={isAdmin}
          catalogRef={catalogRef}
          onSaved={handleProfileSaved}
          complete={canBuy}
          missingFields={buyerMissingFields}
          contactEmailLocked={escrowStatus?.buyer ?? false}
        />

        {/* SIL-379: org-level PUE input — a single facility-efficiency figure,
            independent of the seller/buyer profile split. */}
        <PueSettingsSection
          pue={profile.pue}
          pueUpdatedAt={profile.pueUpdatedAt}
          isAdmin={isAdmin}
          onSaved={handlePueSaved}
        />

        {/* SIL-2169: Escrow.com payments (replaces the RUF-569 mock payment list). */}
        <EscrowPaymentsPanel
          buyerEmail={profile.buyerProfile?.contactEmail}
          sellerEmail={profile.sellerProfile?.contactEmail}
          hasOpenEscrowTransaction={Boolean(escrowStatus?.buyer || escrowStatus?.seller)}
          buyerAwaitingOpsClearance={escrowStatus?.kyb?.buyerAwaitingOpsClearance ?? false}
        />

        {/* SIL-2055: read-only data subscription (business orgs with one). */}
        <DataSubscriptionPanel />

        {/* SIL-2056: org-owned data API keys (admins of business orgs). */}
        <ApiKeysPanel isAdmin={isAdmin} notify={notify} />
      </div>

      {toast && <Toast message={toast.message} type={toast.type} onDismiss={() => setToast(null)} />}
    </>
  );
}
